import { Component, ChangeDetectionStrategy, inject, computed } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { selectQuoteState, selectIsSubscriptionQuote } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";

import { selectFinancingCompanies } from "src/app/store/application/data/financing-company/financing-companies/financing-companies.selectors";
import { selectSalesCreditRoles } from "src/app/store/application/data/sales-credit-role/sales-credit-roles/sales-credit-roles.selectors";
import { selectSalesPersons } from "src/app/store/application/data/sales-person/sales-persons/sales-persons.selectors";
import { CoreCurrencyPipe } from "src/app/shared/pipe/core-currency.pipe";

/**
 * What the order is worth and who gets credit for it.
 *
 * Its own component so the stepper can place it at the very bottom of the dialog, under the
 * destination choice, rather than buried inside the confirmation tab. It is the figures you check
 * last before committing — and creating the order is the point of no return for the sales credit
 * split, since the quote locks afterwards and the shares can no longer be changed.
 *
 * Read-only throughout: it reads the store and renders, nothing else.
 */
@Component({
    selector: "app-create-order-summary",
    templateUrl: "./create-order-summary.component.html",
    styleUrl: "./create-order-summary.component.scss",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, CoreCurrencyPipe],
})
export class CreateOrderSummaryComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quote = toSignal(this.store.select(selectQuoteState));
    private readonly version = toSignal(this.store.select(selectVersion));
    private readonly financingCompanies = toSignal(this.store.select(selectFinancingCompanies), { initialValue: [] });
    private readonly salesCreditRoles = toSignal(this.store.select(selectSalesCreditRoles), { initialValue: [] });
    private readonly salesPersons = toSignal(this.store.select(selectSalesPersons), { initialValue: [] });

    public readonly isSubscription = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });

    // =========================================================================
    // COMPUTED
    // =========================================================================

    /** The two orders a subscription quote produces. */
    public readonly subscriptionSummary = computed(() => {
        const version = this.version();
        const sub = version?.subscription;
        if (!sub) return null;

        const company = this.financingCompanies().find(fc => fc.id === sub.financingCompanyId);
        return {
            financingCompany: company?.name ?? company?.code ?? null,
            // Customer-facing monthly price: totalMonthlyPriceExVat is the one that has the
            // project discount applied (monthlyPriceExVat is the undiscounted list price).
            // Same field the aside footer, summary and the PDF show as "Månadspris".
            monthlyPrice: sub.totalMonthlyPriceInclPlantsExVat,
            // Object value = the recurring product revenue the financing company finances.
            // summary.salesPriceExVat comes from GetQualifyingItems, which already excludes
            // one-time-cost items — so engångskostnader are not double-counted here. NOT
            // estimatedObjectValue, which is only the pre-quote concept estimate.
            objectValue: version.summary?.salesPriceExVat ?? 0,
            // Customer order = startup fee + one-time items + project management + freight.
            customerOneTimeTotal: sub.totalOneTimeFeesExVat,
        };
    });

    /** What the order is worth, for a plain (non-subscription) quote. */
    public readonly orderSummary = computed(() => {
        const summary = this.version()?.summary;
        if (!summary) return null;

        return {
            customer: this.quote()?.customer?.name ?? null,
            orderValueExVat: summary.totalSalesPriceExVat,
            totalIncVat: summary.totalSalesPriceIncVat,
        };
    });

    /**
     * Who gets credit for the deal, and what that is worth in money. The pot is TB3 on the
     * selected version — the same figure the Säljteam card divides.
     *
     * Names and role labels resolve from the core data store, since the rows carry ids only. A
     * salesperson who has since been soft-deleted resolves to a placeholder rather than being
     * dropped: the row still holds a real share.
     */
    public readonly salesTeamSummary = computed(() => {
        const salesInfo = this.quote()?.salesInfo;
        const rows = salesInfo?.salesCredits ?? [];
        if (rows.length === 0) return null;

        const roleById = new Map(this.salesCreditRoles().map(r => [r.id, r]));
        const personById = new Map(this.salesPersons().map(p => [p.id, p]));
        const pot = this.version()?.summary?.grossProfit3 ?? 0;

        return {
            responsible: salesInfo?.responsibleSalesPerson?.name ?? null,
            pot,
            rows: rows.map(row => ({
                salesPersonId: row.salesPersonId,
                name: personById.get(row.salesPersonId)?.name
                    ?? this.translateService.instant("QUOTE.DETAIL.OVERVIEW.SALES_CREDIT.UNKNOWN_SALES_PERSON"),
                // The company the credit was earned in. Creating the order freezes the split, so
                // this is the last chance to see the attribution before it is settled. Falls back
                // to the raw id, which is a human-readable code like "SE01" — a soft-deleted
                // salesperson carries no assignments to resolve a name from.
                company: personById.get(row.salesPersonId)?.companies?.find(c => c.id === row.companyId)?.name
                    ?? row.companyId,
                roles: (row.salesCreditRoleIds ?? [])
                    .map(id => roleById.get(id))
                    .filter(role => !!role)
                    .sort((a, b) => a!.sortOrder - b!.sortOrder)
                    .map(role => role!.name)
                    .join(", "),
                share: row.creditShare,
                amount: pot ? (pot * row.creditShare) / 100 : null,
            })),
        };
    });

    /** Nothing to show at all — a quote with no version summary and no split. */
    public readonly hasContent = computed(() =>
        !!this.subscriptionSummary() || !!this.orderSummary() || !!this.salesTeamSummary(),
    );
}
